/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2024
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/

import React, { useMemo, useState, useEffect, useCallback } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import Markdown from './Markdown';
import { useApi } from '../contexts/ApiHandler';
import { createObjectBlob, findFirstAppWithAccess } from '../utils/common';
import { useAsyncAPIExamplePayload, useAsyncAPIExampleHeader } from 'hooks/asyncAPIExamples';
import { Download } from '@carbon/react/icons';
import { Button, Tabs, Tab, ActionableNotification, Link, TabList, TabPanels, TabPanel } from '@carbon/react';
import { useTranslation, Trans } from 'react-i18next';
import { isKafka as protocolIsKafka } from 'utils/ProtocolTypes';
import { useAsyncAPICodeSnippetLanguages } from 'hooks/asyncAPICodeSnippetLanguages';
import { codeLanguages } from 'code-snippets/constants';
import { apiTypes } from 'code-snippets/constants';
import useGenerateKafkaSnippet from 'code-snippets/useGenerateSnippet';
import { CodeSnippet } from './CodeSnippet';
import { useConfig } from 'contexts/config';
import { uniqueId, isEmpty, has } from 'lodash';
import KafkaPropertiesTable from 'components/KafkaPropertiesTable';
import SchemaView from 'components/SchemaView';
import Dependencies from './Dependencies';
import AsyncapiBindings from './AsyncapiBindings';

const AVRO_FILE_SUFFIX = 'avsc';
const JSON_FILE_SUFFIX = 'json';
const DEFAULT_APITYPE = apiTypes.kafka;

const HeaderSchema = ({ channelName, schema }) => {
  const { loading, example, error } = useAsyncAPIExampleHeader(channelName);

  const props = { schema, exampleLoading: loading, example, exampleError: error };
  return <SchemaView {...props} id="header-schema" data-testid={'header-schema'} />;
};

const PayloadSchema = ({ channelName, operationId }) => {
  const { apiHandler } = useApi();
  const { payload: schema } = useMemo(() => apiHandler.getFirstSchema(channelName, operationId), [apiHandler, channelName, operationId]);

  const { loading, example, error } = useAsyncAPIExamplePayload(channelName, operationId);
  let cleanJson;
  try {
    cleanJson = JSON.stringify(schema?.json(), (key, value) => {
      if (key === 'x-parser-schema-id') {
        return undefined;
      }
      return value;
    }, 2);
  } catch (e) {
    console.error(e)
  } 
  const cleanSchema = cleanJson ? JSON.parse(cleanJson) : null;
  const props = { schema: cleanSchema, exampleLoading: loading, example, exampleError: error };
  return <SchemaView {...props} id="payload-schema" />;
};

const AsyncApiOperationDetail = ({ channelName, operationId }) => {
  // init hooks
  const { apiHandler } = useApi();
  const {
    payload: schemaPayload,
    format: schemaFormat,
    type: schemaType,
    version: schemaVersion,
    encodingType
  } = useMemo(() => apiHandler.getFirstSchema(channelName, operationId), [apiHandler, channelName, operationId]);
  const { schema: headerSchema } = useMemo(() => apiHandler.getFirstMessageHeader(channelName, operationId), [apiHandler, channelName, operationId]);
  const config = useConfig();
  const { t } = useTranslation();
  const appWithAccess = findFirstAppWithAccess(config.apps);
  const channel = apiHandler.api.channels().get(channelName)
  const operationType = channel.operations().get(operationId).action()
  // state
  const [id] = useState(() => uniqueId(`channel-${operationId}`));
  const [selectedCredentials, updateSelectedCredentials] = useState({
    application: appWithAccess ? appWithAccess.name : 'placeholder_app',
    credential: appWithAccess ? appWithAccess.credentials[0].client_id : 'placeholder_credential'
  });
  const [codeLanguage, updateCodeLanguage] = useState(codeLanguages.java);
  const [downloadableContent, updateDownloadableContent] = useState({});
  // vars
  const missingPayload = !schemaPayload || (schemaPayload && isEmpty(schemaPayload));
  const message = useMemo(() => apiHandler.getFirstMessage(channelName, operationId), [apiHandler, channelName, operationId]);
  const isKafka = apiHandler.getFirstProtocol && protocolIsKafka(apiHandler.getFirstProtocol());
  const credentialsExist = has(config, 'apps[0].credentials[0].client_id');
  const isEnforced = useMemo(() => apiHandler.isEnforced(), [apiHandler]);
  const operation = channel.operations().get(operationId)
  // state based hooks
  const { snippet: generatedSnippet, dependencies: generatedDependencies } = useGenerateKafkaSnippet({
    apiType: DEFAULT_APITYPE,
    codeLanguage,
    channelName,
    selectedCredentials,
    operationId
  });
  const availableLanguages = useAsyncAPICodeSnippetLanguages(schemaType, encodingType);

  useEffect(() => {
    if (schemaPayload) {
      updateDownloadableContent(
        createObjectBlob(schemaPayload, channelName, config.removeIBMKeys, apiHandler.isAvroSchema(schemaType) ? AVRO_FILE_SUFFIX : JSON_FILE_SUFFIX)
      );
    }
  }, [schemaPayload, schemaType, channelName, config.removeIBMKeys, apiHandler]);

  const onCodeLanguageChange = (newLanguage) => updateCodeLanguage(newLanguage);

  const onCredentialChange = useCallback(
    (e) => updateSelectedCredentials({ application: e.selectedApplication, credential: e.selectedCredential }),
    [updateSelectedCredentials]
  );
  const renderHeader = () => {
    return (
      <header className="channel__header" data-testid={'channel-data-header'}>
        {t(`channel_${operationType}_operation`, { channelName: operationId ? operationId : channelName })}
      </header>
    );
  };

  const renderDownloader = () => {
    return (
      downloadableContent &&
      Object.values(downloadableContent).length > 0&& (
        <Button
          className="channel__schemaDownload"
          title={t('download_schema')}
          download={downloadableContent.filename}
          href={downloadableContent.fileContent}
          data-testid={'download-schema'}
          kind="ghost"
          renderIcon={Download}
        >
          {t('download_schema')}
        </Button>
      )
    );
  };

  const renderConsume = () => {
    let placeholderWarningMessage = t('login_to_use');
    let showPlaceholderValueWarning = false;
    if (isEnforced) {
      if (!config.options.loggedIn) {
        showPlaceholderValueWarning = true;
        if (config.options.loginLink) {
          placeholderWarningMessage = (
            <Trans i18nKey="login_to_use_with_link">
              <Link href={config.options.loginLink} title={t('login_link_title')}>
                {/* Dev note: The link text is replaced by translation, it's here for linting and as a reminder */}
                Dummy text
              </Link>
            </Trans>
          );
        }
      } else if (!credentialsExist) {
        showPlaceholderValueWarning = true;
        placeholderWarningMessage = t('get_access_to_use');
      }
    }
    const labelId = `${id}-consume`;
    return (
      <div className="channel__information">
        <div role="heading" aria-level={3} className="channel__label" id={labelId}>
          {t('channel_overview_consume')}
        </div>
        <div className="channel__value" role="region" data-testid="channel-overview-consume-code" aria-labelledby={labelId}>
          {showPlaceholderValueWarning ? (
            <ActionableNotification
              inline={true}
              className="channel__login_warning"
              kind={'warning'}
              title={t('placeholder_values')}
              hideCloseButton
              lowContrast
              subtitle={placeholderWarningMessage}
            />
          ) : null}
          <CodeSnippet
            activeCodeLanguage={codeLanguage}
            showExpander
            content={generatedSnippet}
            onChangeCodeLanguage={onCodeLanguageChange}
            availableCodeLanguages={availableLanguages}
            highlight
            withCredentials={isEnforced && credentialsExist}
            onChangeCredentials={onCredentialChange}
            label={t('code_snippet_label')}
          />
          {generatedDependencies && <Dependencies dependencyContent={generatedDependencies} activeCodeLanguage={codeLanguage} label={t('dependencies')} />}
          {schemaFormat && !missingPayload && renderDownloader()}
        </div>
      </div>
    );
  };

  const renderMessageDescription = () => {
    const { title, summary, description } = message;
    const markupType = apiHandler.getMarkupType();
    const descriptionJsx = description ? <Markdown data={description} markupType={markupType} /> : null;
    const titleProps = { className: 'channel__title', 'aria-label': t('title') };
    const summaryProps = { className: 'channel__summary', 'data-testid': 'summary' };
    const titleJsx = title ? <p {...titleProps}>{title}</p> : null;
    const summaryJsx = summary ? <p {...summaryProps}>{summary}</p> : descriptionJsx ? React.cloneElement(descriptionJsx, summaryProps) : null;

    return (
      <div data-testid={'message-description'}>
        {titleJsx}
        {summaryJsx}
        {summary && description ? descriptionJsx : null}
      </div>
    );
  };

  const renderBody = () => {
    if (isKafka && operation.action() !== 'publish' && operation.action() !== 'send') {
      //TODO: Add Publish code snippets for publish AsyncAPI protocols when available
      return (
        <Tabs className={'channel__tabs'}>
          <TabList aria-label="channel tabs">
            <Tab id="instructions" title={t('instructions')}>
              {t('instructions')}
            </Tab>
            <Tab id="channel-references" title={t('references')} data-testid={'channel-references'}>
              {t('references')}
            </Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              <div className="channel__body divided">
                {renderConsume()}
                {<KafkaPropertiesTable data-testid={'kafka_properties'} channelName={channelName} operationId={operationId} />}
              </div>
            </TabPanel>
            <TabPanel>
              {renderHeaderSchema(headerSchema)}
              {renderPayloadSchema()}
            </TabPanel>
          </TabPanels>
        </Tabs>
      );
    } else {
      return (
        <>
          {renderBindings()}
          {renderHeaderSchema(headerSchema)}
          {renderPayloadSchema()}
        </>
      );
    }
  };

  const renderHeaderSchema = (schema) => {
    if (schema) {
      return (
        <div className="channel__body divided">
          <div className={'channel__information'}>
            <div role="heading" aria-level={3} className="channel__label">{t('header_schema')}</div>
            <div className="channel__value">
              <HeaderSchema channelName={channelName} schema={schema} />
            </div>
          </div>
        </div>
      );
    }
  };

  const renderBindings = () => {
    return (
      message.bindings && (
        <div className="message-bindings">
          <AsyncapiBindings bindings={message.bindings} />
        </div>
      )
    );
  };

  const renderPayloadSchema = () => {
    const payloadId = uniqueId('payload_schema');

    return (
      <div className="channel__body divided">
        <div className={'channel__information'}>
          <div role="heading" aria-level={3} className="channel__label" id={payloadId}>
            {t('payload_schema')}
          </div>
          <div role="region" className="channel__value" data-testid="channel-schema" aria-labelledby={payloadId}>
            <PayloadSchema operationId={operationId} channelName={channelName} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className={classNames('channel', 'main')}>
      {renderHeader()}
      {schemaType && schemaVersion && (
        <p className={'channel__schemaVersion'} data-testid="channel-schema-format">
          {schemaType} {schemaVersion}
        </p>
      )}
      {renderMessageDescription()}
      {renderBody()}
    </section>
  );
};

AsyncApiOperationDetail.propTypes = {
  channelName: PropTypes.string
};

export default AsyncApiOperationDetail;
